/**
 * The names a session holds for things it ranked that are not workspace items.
 *
 * A session can rank three kinds of thing that the `items` table knows nothing
 * about:
 *   - items typed into the Event Designer, kept in `mode_config.v3Config.items`
 *     with generated ids such as `item_9rbxi1e`;
 *   - criteria ranked as items, kept in `mode_config.v3Config.criteria`
 *     (ids such as `crit_xxxxx`);
 *   - typed lines of an isolated session (`isolated_items`), whose id is the
 *     text itself.
 *
 * Workspace items are looked up by the caller (the store on screen, the
 * `items` table on the server); this covers the rest, so every screen that
 * names a session's ranked rows (the results page, the memo) reads the same
 * names. Before this, the results page showed the generated ids and the memo
 * showed "Untitled item".
 */
export function sessionOwnItemTitles(session: {
  modeConfig?: unknown;
  isolatedItems?: readonly string[] | null;
}): Record<string, string> {
  const titles: Record<string, string> = {};

  for (const line of session.isolatedItems ?? []) {
    const text = typeof line === 'string' ? line.trim() : '';
    if (text) titles[text] = text;
  }

  const v3 = (session.modeConfig as { v3Config?: unknown } | null | undefined)?.v3Config as
    | {
        items?: Array<{ id?: unknown; title?: unknown; name?: unknown }>;
        criteria?: Array<{ id?: unknown; name?: unknown }>;
      }
    | undefined;

  for (const c of Array.isArray(v3?.criteria) ? v3.criteria : []) {
    if (typeof c?.id === 'string' && typeof c.name === 'string' && c.name.trim()) titles[c.id] = c.name;
  }
  for (const i of Array.isArray(v3?.items) ? v3.items : []) {
    if (typeof i?.id !== 'string') continue;
    const name = typeof i.title === 'string' && i.title.trim() ? i.title : typeof i.name === 'string' ? i.name : '';
    if (name.trim()) titles[i.id] = name;
  }

  return titles;
}
